import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import { useNavigate as useRouterNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader, PageContent, useNotification } from '@viilutalous/common';
import { myTasksAPI } from '@viilutalous/projects';
import { useTenant } from '../contexts/TenantContext';
import { useMeHost } from '../contexts/MeHostContext';
import { format, parseISO } from 'date-fns';

export default function MyTasks() {
  const { t } = useTranslation();
  const navigate = useRouterNavigate();
  const { tenants, selectTenant } = useTenant();
  const { hostId, hosts } = useMeHost();
  const { showError } = useNotification();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const tenantById = useMemo(() => {
    const m = new Map();
    for (const tnt of tenants) m.set(tnt.id, tnt);
    return m;
  }, [tenants]);

  const load = useCallback(async () => {
    if (!hostId) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const res = await myTasksAPI.getOpenAcrossHost(hostId);
      setRows(res.data?.tasks || []);
    } catch (err) {
      showError(err?.response?.data?.detail || t('common.error', 'Error'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [hostId, showError, t]);

  useEffect(() => { load(); }, [load]);

  const openTask = (task) => {
    const slug = task.tenant_slug || tenantById.get(task.tenant_id)?.slug;
    if (!slug || !task.project_id) return;
    const tnt = tenantById.get(task.tenant_id);
    if (tnt) selectTenant(tnt);
    navigate(`/app/${slug}/projects/${task.project_id}/tasks/${task.id}`);
  };

  const formatDeadline = (iso) => {
    if (!iso) return '';
    try { return format(parseISO(iso), 'dd.MM.yyyy'); } catch { return iso; }
  };

  return (
    <>
      <PageHeader />
      <PageContent>
        {loading && (
          <Stack direction="row" justifyContent="center" sx={{ mb: 2 }}>
            <CircularProgress size={20} />
          </Stack>
        )}

        {hosts.length === 0 && (
          <Alert severity="info">{t('me.tasks.no_hosts', 'You have no tenant access yet.')}</Alert>
        )}

        {hosts.length > 0 && rows.length === 0 && !loading && (
          <Alert severity="success">{t('me.tasks.empty', 'No open tasks assigned to you.')}</Alert>
        )}

        {rows.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('me.tasks.tenant', 'Tenant')}</TableCell>
                <TableCell>{t('me.tasks.identifier', 'ID')}</TableCell>
                <TableCell>{t('me.tasks.name', 'Task')}</TableCell>
                <TableCell>{t('me.tasks.stage', 'Stage')}</TableCell>
                <TableCell>{t('me.tasks.deadline', 'Deadline')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((task) => (
                <TableRow key={task.id} hover sx={{ cursor: 'pointer' }} onClick={() => openTask(task)}>
                  <TableCell>{task.tenant_name || tenantById.get(task.tenant_id)?.name || task.tenant_id}</TableCell>
                  <TableCell><Chip size="small" label={task.identifier} variant="outlined" /></TableCell>
                  <TableCell>{task.name}</TableCell>
                  <TableCell>{task.stage_name || task.stage_key || ''}</TableCell>
                  <TableCell>{formatDeadline(task.date_deadline)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </PageContent>
    </>
  );
}
